import findIndexByInstanceKey from '../actions/notifications/helpers/findIndexByInstanceKey';
import getAllTableViewsContainingItemsForFolder from '../utils/getAllTableViewsContainingItemsForFolder';
import TableOperations from 'owa-mail-list-table-operations';
import tombstoneOperations, { TombstoneReasonType } from 'owa-mail-list-tombstone';
import { actionNames, EmptyFolderStoreUpdate } from 'owa-mail-store-actions';
import { Folder } from 'owa-folders';
import { getLastDeliveryTimeStamp } from 'owa-mail-list-store/lib/selectors/mailRowDataPropertyGetter';
import { MailListItemSelectionSource } from 'owa-mail-store';
import { subscribe } from 'satcheljs/lib/legacy/stitch';
import { TableView } from 'owa-mail-list-store';
import { resetSelection } from 'owa-mail-actions';

/**
 * Subscribe the EmptyFolderStoreUpdate stitch and updates the list view store when the stitch is raised.
 */
export default function emptyFolderStitch() {
    subscribe<EmptyFolderStoreUpdate>(
        actionNames.EmptyFolderStoreUpdate,
        (
            folder: Folder,
            clientSyncTime: Date,
            deletedItemsFolderId: string,
            rowKeysToExclude: string[]
        ) => {
            const tableViewsForCurrentFolder = getAllTableViewsContainingItemsForFolder(
                folder.FolderId.Id,
                false /* shouldIncludeSearchTable */
            );

            // TODO: 1300 We need to find a way to invalidate tableView.lastEmptiedTime
            // in case something goes wrong with snapshot, etc.
            tableViewsForCurrentFolder.forEach(tableView => {
                // We need to wait to clear table view until AFTER
                // we have set last emptied time AND tombstoned.
                setLastEmptiedTime(tableView, rowKeysToExclude);
                tombstoneNotExcludedItems(tableView, rowKeysToExclude);

                TableOperations.clear(
                    tableView,
                    clientSyncTime /* skipRowsNewerThanTime */,
                    rowKeysToExclude
                );
                // Update the tableView's total count
                tableView.totalRowsInView = tableView.rowKeys.length;

                // Reset selection for this folder.
                // VSO 13163 - Do not call resetSelection after data is removed in MultiSelectRP
                resetSelection(tableView, MailListItemSelectionSource.Reset);
            });
        }
    );
}

// Set lastEmptiedTime on tableview
// Sets the value to the last delivery timestamp of oldest item in exclusion list if the list is not empty
// else sets current time
function setLastEmptiedTime(tableView: TableView, rowKeysToExclude: string[]) {
    let oldestItemDeliveryTimeStamp = null;

    if (rowKeysToExclude != undefined) {
        // Find item with oldest lastDeliveryTimeStamp
        for (let i = 0; i < rowKeysToExclude.length; i++) {
            const lastDeliveryTimeStamp = getLastDeliveryTimeStamp(rowKeysToExclude[i], tableView);
            if (
                oldestItemDeliveryTimeStamp == null ||
                new Date(lastDeliveryTimeStamp) < new Date(oldestItemDeliveryTimeStamp)
            ) {
                oldestItemDeliveryTimeStamp = lastDeliveryTimeStamp;
            }
        }
    }

    // Set lastEmptiedTime to the timestamp of oldest item in exclusion list
    if (oldestItemDeliveryTimeStamp != null) {
        tableView.lastEmptiedTime = new Date(oldestItemDeliveryTimeStamp);
    } else {
        tableView.lastEmptiedTime = new Date();
    }
}

// Add selected items newer than lastEmptiedTime to tombstone list
function tombstoneNotExcludedItems(tableView: TableView, rowKeysToExclude: string[]) {
    if (rowKeysToExclude == undefined) {
        return;
    }

    let lastUnselectedRowIndex = 0;

    // Find index of last unselected row
    for (let i = 0; i < rowKeysToExclude.length; i++) {
        const index = findIndexByInstanceKey(rowKeysToExclude[i], tableView);

        if (index > lastUnselectedRowIndex) {
            lastUnselectedRowIndex = index;
        }
    }

    // Add all selected rows from index 0 to lastUnselectedRowIndex to tombstone
    const toTombstone: string[] = tableView.rowKeys
        .slice(0, lastUnselectedRowIndex)
        .filter(key => rowKeysToExclude.indexOf(key) == -1);

    tombstoneOperations.add(toTombstone, tableView.serverFolderId, TombstoneReasonType.RowRemove);
}
